import {
  CheckCircleOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Descriptions,
  Empty,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import {
  activateSubscription,
  cancelSubscription,
  getAllSubscriptions,
  getSubscriptionInvoices,
  resumeSubscription,
  retrySubscriptionPayment,
} from "../actions/subscription_actions";
import { describeRunningDiscount, discountedAmount } from "../utils/promo";

const statusColors = {
  active: "green",
  inactive: "gold",
  past_due: "red",
  cancelling: "orange",
  cancelled: "default",
  overdue: "red",
};

const normalizeStatus = (status) => {
  const statuses = {
    PENDING_ACTIVATION: "inactive",
    ACTIVE: "active",
    PAST_DUE: "past_due",
    CANCELLING: "cancelling",
    CANCELED: "cancelled",
    CANCELLED: "cancelled",
    UNPAID: "overdue",
  };
  return (
    statuses[String(status || "").toUpperCase()] ||
    String(status || "").toLowerCase()
  );
};

const subscriptionRecords = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item?.id);
  if (Array.isArray(value?.docs)) return value.docs.filter((item) => item?.id);
  if (Array.isArray(value?.data)) return value.data.filter((item) => item?.id);
  return [];
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

const formatMajorMoney = (amount, currency = "USD") => {
  if (amount == null) return "-";
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
  }).format(Number(amount));
};

const formatMinorMoney = (amount, currency = "USD") => {
  if (amount == null) return "-";
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
  }).format(Number(amount) / 100);
};

export default function SubscriptionDetailsModal({
  open,
  onClose,
  subscription,
  accountUrn,
  accountName,
  businessName,
  canManage = true,
  onUpdated,
}) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const shouldLoadAccount = open && !!accountUrn;
  const {
    data: accountSubscriptions = [],
    isLoading: subscriptionsLoading,
    isError: subscriptionsError,
  } = useQuery({
    queryKey: ["ops-account-subscriptions", accountUrn],
    queryFn: async () => {
      const result = await getAllSubscriptions({
        page: 1,
        limit: 100,
        accountIds: accountUrn,
      });
      return result?.docs || [];
    },
    enabled: shouldLoadAccount,
    retry: false,
  });
  const availableSubscriptions = useMemo(() => {
    const loadedSubscriptions = subscriptionRecords(accountSubscriptions);
    if (!subscription?.id) return loadedSubscriptions;

    const selectedIndex = loadedSubscriptions.findIndex(
      (item) => item.id === subscription.id,
    );
    if (selectedIndex < 0) return [subscription, ...loadedSubscriptions];

    return loadedSubscriptions.map((item, index) =>
      index === selectedIndex ? { ...item, ...subscription } : item,
    );
  }, [subscription, accountSubscriptions]);

  useEffect(() => {
    if (!open) return;
    const preferredId =
      subscription?.id || availableSubscriptions[0]?.id || null;
    setSelectedId(preferredId);
  }, [open, subscription, availableSubscriptions]);

  const activeSubscription =
    availableSubscriptions.find((item) => item.id === selectedId) ||
    availableSubscriptions[0] ||
    null;
  const subscriptionId = activeSubscription?.id;
  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ["ops-subscription-invoices", subscriptionId],
    queryFn: () => getSubscriptionInvoices(subscriptionId),
    enabled: open && !!subscriptionId,
    retry: false,
  });

  const lifecycleMutation = useMutation({
    mutationFn: ({ action, id }) => {
      if (action === "activate") return activateSubscription(id);
      if (action === "cancel") return cancelSubscription(id);
      if (action === "resume") return resumeSubscription(id);
      return retrySubscriptionPayment(id);
    },
    onSuccess: (result, variables) => {
      const messages = {
        activate: "Subscription activation queued.",
        cancel: "Cancellation scheduled for period end.",
        resume: "Auto-renewal resume queued.",
        retry: "Payment retry queued.",
      };
      message.success(result?.message || messages[variables.action]);
      queryClient.invalidateQueries({ queryKey: ["ops-subscriptions"] });
      queryClient.invalidateQueries({
        queryKey: ["ops-account-subscriptions", accountUrn],
      });
      queryClient.invalidateQueries({
        queryKey: ["ops-subscription-invoices", subscriptionId],
      });
      onUpdated?.();
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Subscription operation failed.",
      );
    },
  });

  const runAction = (action) => {
    if (!subscriptionId) return;
    lifecycleMutation.mutate({ action, id: subscriptionId });
  };

  const invoiceColumns = [
    {
      title: "Invoice",
      dataIndex: "stripe_invoice_id",
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag
          color={
            status === "paid" ? "green" : status === "pending" ? "gold" : "red"
          }
        >
          {(status || "unknown").toUpperCase()}
        </Tag>
      ),
    },
    { title: "Billing date", dataIndex: "billing_date", render: formatDate },
    {
      title: "Amount",
      render: (_, row) => formatMinorMoney(row.amount, row.currency),
    },
    {
      title: "Files",
      render: (_, row) => (
        <Space>
          {row.stripe_invoice_url && (
            <a href={row.stripe_invoice_url} target="_blank" rel="noreferrer">
              View
            </a>
          )}
          {row.invoice_pdf_url && (
            <a href={row.invoice_pdf_url} target="_blank" rel="noreferrer">
              PDF
            </a>
          )}
          {!row.stripe_invoice_url && !row.invoice_pdf_url && "-"}
        </Space>
      ),
    },
  ];

  const status = normalizeStatus(activeSubscription?.status);
  const actionLoading = lifecycleMutation.isPending;
  const businesses = activeSubscription?.businesses || [];
  const displayAccountName =
    activeSubscription?.account_name ||
    accountName ||
    activeSubscription?.account_slug;

  return (
    <Modal
      className="subscription-details-modal"
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      title={
        displayAccountName
          ? `Account billing - ${displayAccountName}`
          : "Account billing"
      }
      destroyOnClose
    >
      {subscriptionsLoading ? (
        <div style={{ minHeight: 220, display: "grid", placeItems: "center" }}>
          <Spin size="large" />
        </div>
      ) : subscriptionsError ? (
        <Alert
          type="error"
          message="Could not load billing for this account."
        />
      ) : !activeSubscription ? (
        <Empty description="No billing subscription found for this account." />
      ) : (
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          {availableSubscriptions.length > 1 && (
            <Select
              value={activeSubscription.id}
              onChange={setSelectedId}
              style={{ width: "100%" }}
              options={availableSubscriptions.map((item) => ({
                value: item.id,
                label: `${item.plan_name || item.name || "Subscription"} - ${item.status}`,
              }))}
            />
          )}

          <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2 }}>
            <Descriptions.Item label="Status">
              <Tag color={statusColors[status] || "default"}>
                {(status || "unknown").toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Plan">
              {activeSubscription.plan_name ||
                activeSubscription.planName ||
                activeSubscription.name ||
                "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Account">
              <div className="subscription-account-value">
                {displayAccountName && <strong>{displayAccountName}</strong>}
                <span>
                  {accountUrn || activeSubscription.account_urn || "-"}
                </span>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Businesses">
              {businesses.length
                ? `${businesses.length} linked`
                : businessName
                  ? "1 linked"
                  : "None linked"}
            </Descriptions.Item>
            <Descriptions.Item label="Period">
              {activeSubscription.period || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Recurring amount">
              {formatMajorMoney(
                activeSubscription.amount,
                activeSubscription.currency,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Sites">
              {activeSubscription.sites_count || "-"}
            </Descriptions.Item>
            {activeSubscription.discount && (
              <Descriptions.Item label="Promo" span={2}>
                <div>{describeRunningDiscount(activeSubscription.discount)}</div>
                {activeSubscription.discount.status === "ACTIVE" &&
                  activeSubscription.discount.remaining_cycles > 0 &&
                  ["active", "past_due", "overdue"].includes(status) &&
                  activeSubscription.amount != null && (
                    <div className="subscription-secondary">
                      Next charge about{" "}
                      {formatMajorMoney(
                        discountedAmount(activeSubscription.amount, activeSubscription.discount.percent_off),
                        activeSubscription.currency,
                      )}{" "}
                      instead of{" "}
                      {formatMajorMoney(activeSubscription.amount, activeSubscription.currency)}
                    </div>
                  )}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Next billing">
              {formatDate(
                activeSubscription.nextBillingDate ||
                  activeSubscription.next_billing_date,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Subscription ID" span={2}>
              <span className="subscription-id-value">
                {activeSubscription.id}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Covered businesses" span={2}>
              <Space wrap className="subscription-business-list">
                {businesses.length ? (
                  businesses.map((business) => (
                    <Tag
                      key={
                        business.business_urn || business.urn || business.slug
                      }
                    >
                      {business.name || business.slug}
                    </Tag>
                  ))
                ) : businessName ? (
                  <Tag>{businessName}</Tag>
                ) : (
                  "No businesses linked"
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>

          {canManage && (
            <Space wrap>
              {status === "inactive" && (
                <>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={actionLoading}
                    onClick={() => runAction("activate")}
                  >
                    Approve and activate
                  </Button>
                  {/* <Popconfirm
                  title="Cancel this paid signup before activation?"
                  okText="Cancel signup"
                  onConfirm={() => runAction("cancel")}
                >
                  <Button danger icon={<StopOutlined />} loading={actionLoading}>
                    Cancel signup
                  </Button>
                </Popconfirm> */}
                </>
              )}
              {["active", "past_due", "overdue"].includes(status) && (
                <Popconfirm
                  title="Cancel this subscription at the end of its current period?"
                  okText="Schedule cancellation"
                  onConfirm={() => runAction("cancel")}
                >
                  <Button
                    danger
                    icon={<StopOutlined />}
                    loading={actionLoading}
                  >
                    Cancel at period end
                  </Button>
                </Popconfirm>
              )}
              {status === "cancelling" && (
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={actionLoading}
                  onClick={() => runAction("resume")}
                >
                  Resume auto-renewal
                </Button>
              )}
              {["active", "cancelling"].includes(status) && (
                <Button
                  icon={<CheckCircleOutlined />}
                  loading={actionLoading}
                  onClick={() => runAction("activate")}
                >
                  Refresh account access
                </Button>
              )}
              {["past_due", "overdue"].includes(status) && (
                <Button
                  icon={<ReloadOutlined />}
                  loading={actionLoading}
                  onClick={() => runAction("retry")}
                >
                  Retry payment
                </Button>
              )}
            </Space>
          )}

          <Table
            columns={invoiceColumns}
            dataSource={invoices}
            loading={invoicesLoading}
            rowKey={(row) => row.id || row.stripe_invoice_id}
            pagination={{ pageSize: 6, hideOnSinglePage: true }}
            scroll={{ x: 760 }}
            size="small"
          />
        </Space>
      )}
    </Modal>
  );
}
